import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Sparkles, Plus, Check, Upload, BookOpen, ArrowRight } from 'lucide-react';
import { api, formatApiError, BACKEND_URL } from '../../lib/api';
import { MusicPicker } from '../../components/MusicPicker';
import { SlideshowPhotoPicker } from '../../components/SlideshowPhotoPicker';
import { PartialDateInput, formatPartialDate } from '../../components/PartialDateInput';
import { toast } from 'sonner';

export function VideoTab({ memorialId, onOpenBook }) {
  const [info, setInfo] = useState(null);
  const [generating, setGenerating] = useState(false);
  // One soundtrack drives both style renders — simpler UX, and the
  // full-width music rows need real estate that we can't get inside a
  // split style card.
  const [music, setMusic] = useState('nuvole');
  const pollRef = useRef(null);

  // iter95.9m — Resilient load + visible diagnostic. The previous
  // `load()` had no `.catch()`, so a single failed GET /video (token
  // refresh, 500, CORS, network blip) would silently leave `info` stuck
  // at the last "regenerating" snapshot — UI spins forever even though
  // the actual render finished. Now we surface every failure to the
  // user, count consecutive failures, and stop polling after 5 in a
  // row so the family never gets trapped.
  const [loadError, setLoadError] = useState(null);
  const failCountRef = useRef(0);
  const load = useCallback(async () => {
    try {
      // Cache-bust so an aggressive CDN never serves stale "regenerating".
      const { data } = await api.get(
        `/memorials/${memorialId}/video?_=${Date.now()}`
      );
      setInfo(data);
      setLoadError(null);
      failCountRef.current = 0;
      if (data && data.default_music_track) {
        const saved = data.default_music_track;
        setMusic(saved.startsWith('custom:') ? '__custom__' : saved);
      }
    } catch (e) {
      failCountRef.current += 1;
      const msg = formatApiError(e) || 'Could not reach the server.';
      setLoadError({
        message: msg,
        attempts: failCountRef.current,
        ts: new Date().toISOString(),
      });
      if (failCountRef.current >= 5) {
        // Stop polling so we don't hammer a broken backend.
        if (pollRef.current) clearInterval(pollRef.current);
      }
    }
  }, [memorialId]);
  useEffect(() => { load(); }, [load]);

  // Poll while EITHER style is still busy. The /video response is
  // `{animated: {status}, slideshow: {status}}` — there is no top-level
  // `status`, which is why the previous keying off `info.status` never
  // triggered and the UI sat on "Creating…" forever.
  useEffect(() => {
    if (!info) return undefined;
    const busy =
      info.animated?.status === 'regenerating' ||
      info.animated?.status === 'queued' ||
      info.slideshow?.status === 'regenerating' ||
      info.slideshow?.status === 'queued';
    if (!busy) return undefined;
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [info, load]);

  const generate = async (style = 'animated') => {
    setGenerating(true);
    try {
      await api.post(`/memorials/${memorialId}/video/generate?style=${style}&music_track=${encodeURIComponent(music)}`);
      await load();
    } catch (e) { alert(formatApiError(e)); } finally { setGenerating(false); }
  };

  const restoreRender = async (style, render_id) => {
    try {
      await api.post(`/memorials/${memorialId}/video/restore`, { style, render_id });
      await load();
      toast.success('Earlier version restored as current');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abandonRender = async () => {
    // Defensive confirmation — abandoning kills an in-flight render
    // immediately, but it never deletes a previously-completed video.
    // The backend (iter95.9l) restores `ready` status if a prior
    // `storage_path` is on file.
    const ok = typeof window !== 'undefined'
      ? window.confirm(
          'Cancel the current render?\n\n' +
          'This will immediately stop the "Creating…" spinner so you can ' +
          'start a fresh render. Any previously-completed video for this ' +
          'memorial will remain available — only the in-flight render is ' +
          'cancelled.'
        )
      : true;
    if (!ok) return;
    try {
      const { data } = await api.post(`/memorials/${memorialId}/video/abandon`);
      await load();
      const restored = (data && data.restored_styles) || [];
      if (restored.length > 0) {
        toast.success(
          'Render cancelled — your previous video is still available.'
        );
      } else {
        toast.success(
          'Render cancelled. You can start a fresh one now.'
        );
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePublic = async (next) => {
    try {
      await api.patch(`/memorials/${memorialId}`, { public_video_enabled: next });
      await load();
      toast.success(next ? 'Video is now visible on the public memorial' : 'Video hidden from public memorial');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPublicStyle = async (style) => {
    try {
      await api.patch(`/memorials/${memorialId}/public-video-style`, { public_video_style: style });
      await load();
      toast.success(`Public tribute set to ${style === 'animated' ? 'Animated Journey' : 'Photo Slideshow'}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!info) return <div className="ml-card">Loading…</div>;

  const animated = info.animated || {};
  const slideshow = info.slideshow || {};
  const currentStyle = info.public_video_style || 'animated';
  const anyBusy = [animated.status, slideshow.status].some((s) => s === 'regenerating' || s === 'queued');

  return (
    <div className="space-y-5" data-testid="video-tab">
      <div className="ml-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="ml-overline">Life Journey Video</div>
            <h2 className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">Choose how you'd like to remember them.</h2>
          </div>
          <span
            className="text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/50 rounded-full px-3 py-1"
            data-testid="video-experimental-badge"
          >
            Preview · Experimental
          </span>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
          Two tributes, no AI. Render either or both, then choose which plays on the public memorial.
        </p>
        {/* iter95.9m — Always-visible status strip so families never sit
            blind in front of a spinner. Shows the raw render status,
            elapsed time, last-poll error (if any), and lets the user
            force-refresh the status without reloading the whole page. */}
        <div
          className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] border-t border-[hsl(var(--border))] pt-3"
          data-testid="video-status-strip"
        >
          <span className="uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Render status</span>
          <span data-testid="status-slideshow">
            <span className="text-[hsl(var(--muted-foreground))]">slideshow:</span>{' '}
            <strong className={
              slideshow.status === 'ready' ? 'text-green-700'
              : slideshow.status === 'failed' ? 'text-red-700'
              : slideshow.status === 'regenerating' || slideshow.status === 'queued' ? 'text-amber-700'
              : 'text-[hsl(var(--muted-foreground))]'}>
              {slideshow.status || 'none'}
            </strong>
            {slideshow.job_id && <span className="text-[hsl(var(--muted-foreground))]"> ({slideshow.job_id})</span>}
          </span>
          <span data-testid="status-animated">
            <span className="text-[hsl(var(--muted-foreground))]">animated:</span>{' '}
            <strong className={
              animated.status === 'ready' ? 'text-green-700'
              : animated.status === 'failed' ? 'text-red-700'
              : animated.status === 'regenerating' || animated.status === 'queued' ? 'text-amber-700'
              : 'text-[hsl(var(--muted-foreground))]'}>
              {animated.status || 'none'}
            </strong>
          </span>
          <button
            type="button"
            onClick={() => load()}
            className="underline text-[hsl(var(--primary))] hover:opacity-70"
            data-testid="video-status-refresh"
          >
            Refresh status now
          </button>
          {loadError && (
            <span
              className="text-red-700 font-medium"
              data-testid="video-load-error"
              title={loadError.ts}
            >
              ⚠ Status poll failed ({loadError.attempts}× in a row): {loadError.message}
            </span>
          )}
        </div>
        {/* If either style has a failure reason, surface it prominently
            so the user knows exactly WHY their render failed instead of
            seeing a generic "Render failed" and clicking Generate again
            into the same trap. */}
        {(slideshow.error || animated.error) && (
          <div
            className="mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
            data-testid="video-error-banner"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              ⚠ Last render failed — here{"'"}s why:
            </div>
            {slideshow.error && (
              <div className="mt-1"><strong>Slideshow:</strong> {slideshow.error}</div>
            )}
            {animated.error && (
              <div className="mt-1"><strong>Animated:</strong> {animated.error}</div>
            )}
            {(slideshow.music_track || '').startsWith('custom:') && (
              <div className="mt-2 text-xs italic">
                Tip: this render used your custom uploaded music track. If failures
                keep happening, switch the music picker below to a library track
                (Elegia, Nuvole, etc.) and try Generate again — corrupted or
                unusual MP3 encodings are a common cause of slideshow failures.
              </div>
            )}
          </div>
        )}
      </div>

      {/* iter95.9d — Memory Book entry card inside Life Journey */}
      <button
        type="button"
        onClick={onOpenBook}
        className="ml-card !p-0 group text-left w-full overflow-hidden border-[hsl(var(--accent))]/40 hover:border-[hsl(var(--accent))] transition-colors"
        data-testid="memory-book-entry-card"
      >
        <div className="flex items-start gap-4 p-5">
          <div className="w-12 h-12 rounded-lg bg-[hsl(var(--accent))]/15 flex items-center justify-center shrink-0">
            <BookOpen className="w-6 h-6 text-[hsl(var(--accent))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="ml-overline">Memory Book · NEW</div>
            <div className="font-serif text-lg mt-1 text-[hsl(var(--primary))]">
              A premium photo storybook of their life
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 leading-relaxed">
              Write the story behind each photo. Hero photos become full-page features, smaller
              moments go two-per-page. Generate a downloadable PDF with cover, dedication, and
              family messages — ready for a future hardcover edition.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-[hsl(var(--accent))] mt-2 shrink-0 group-hover:translate-x-1 transition-transform" />
        </div>
      </button>

      {info.length_drivers && (
        <LengthDriversPanel drivers={info.length_drivers} />
      )}

      <CustomVideoMusicCard
        memorialId={memorialId}
        customMusic={info.custom_music}
        defaultMusicTrack={info.default_music_track}
        onChange={load}
        onSetDefault={async () => {
          try {
            await api.patch(`/memorials/${memorialId}`, {
              default_music_track: `custom:${memorialId}`,
            });
            // Reflect immediately in the picker UI so the active row is the
            // custom one — and persist for next session/device/admin.
            setMusic('__custom__');
            await load();
            toast.success('Saved — your uploaded music is now the default soundtrack.');
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />

      {/* One soundtrack drives both Portrait Tribute and Photo Slideshow.
          Rendered at the memorial level so rows can be wide and premium. */}
      <div className="ml-card" data-testid="video-music-picker-card">
        <MusicPicker
          value={music}
          onChange={(next) => {
            if (next === music) return;  // no-op selecting the same row
            setMusic(next);
            // Persist immediately so reload / other admins keep the choice.
            // Map UI "__custom__" → backend "custom:{memorial_id}".
            const persisted = next === '__custom__' ? `custom:${memorialId}` : next;
            api.patch(`/memorials/${memorialId}`, { default_music_track: persisted })
              .catch(() => { /* non-fatal — next render still wins */ });
            // Surface that existing renders need to be regenerated to pick
            // up the new audio — the StyleCard banner reinforces this.
            toast('Soundtrack updated. Regenerate the video to apply.', {
              duration: 4000,
            });
          }}
          testIdPrefix="music"
          customTrack={info.custom_music ? {
            filename: info.custom_music.filename,
            preview_url: `/api/memorials/${memorialId}/music/custom.mp3`,
          } : null}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <StyleCard
          title="Portrait Tribute"
          subtitle="Their photograph held still, with the Life Story fading chapter by chapter — no facial animation, ever."
          style="animated"
          info={animated}
          currentPublic={currentStyle}
          currentMusic={music}
          onGenerate={() => generate('animated')}
          onSetPublic={() => setPublicStyle('animated')}
          onRestore={(rid) => restoreRender('animated', rid)}
          onAbandon={abandonRender}
          generating={generating}
          anyBusy={anyBusy}
          memorialId={memorialId}
        />
        <StyleCard
          title="Photo Slideshow"
          subtitle="A quiet Ken-Burns tribute using the photos you select below."
          style="slideshow"
          info={slideshow}
          currentPublic={currentStyle}
          currentMusic={music}
          onGenerate={() => generate('slideshow')}
          onSetPublic={() => setPublicStyle('slideshow')}
          onRestore={(rid) => restoreRender('slideshow', rid)}
          onAbandon={abandonRender}
          generating={generating}
          anyBusy={anyBusy}
          memorialId={memorialId}
        />
      </div>

      {/* Public visibility toggle — default OFF (experimental) */}
      <div className="ml-card flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="ml-overline">Visibility</div>
          <div className="font-serif text-lg mt-1 text-[hsl(var(--primary))]">Show Life Journey Video Publicly</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-md">
            Off by default while the video is in preview. When enabled, the {currentStyle === 'animated' ? 'animated journey' : 'photo slideshow'} will play at the top of the public memorial page.
          </div>
        </div>
        <button
          onClick={() => togglePublic(!info.public_video_enabled)}
          role="switch"
          aria-checked={!!info.public_video_enabled}
          data-testid="public-video-toggle"
          className={`relative shrink-0 w-14 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/50 ${
            info.public_video_enabled ? 'bg-[hsl(var(--accent))]' : 'bg-border'
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
              info.public_video_enabled ? 'translate-x-6' : ''
            }`}
          />
        </button>
      </div>

      {/* Life events editor — add/edit/delete */}
      <LifeEventsEditor memorialId={memorialId} onChange={load} />
    </div>
  );
}

function StyleCard({ title, subtitle, style, info, currentPublic, currentMusic, onGenerate, onSetPublic, onRestore, onAbandon, generating, anyBusy, memorialId }) {
  const BACKEND = BACKEND_URL;
  const isBusy = info.status === 'regenerating' || info.status === 'queued';
  const isFailed = info.status === 'failed';
  const hasVideo = info.status === 'ready' && info.video_url;
  // Elapsed time since the active job started — drives the long-running
  // copy + the live "rendering for Ns" badge so the user knows the page
  // isn't frozen. iter95.9c.
  const [elapsedSec, setElapsedSec] = React.useState(0);
  React.useEffect(() => {
    if (!isBusy) { setElapsedSec(0); return undefined; }
    const startedAt = info.job?.started_at || info.job?.created_at;
    const startMs = startedAt ? Date.parse(startedAt) : Date.now();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isBusy, info.job?.started_at, info.job?.created_at]);
  const videoSrc = hasVideo
    ? `${BACKEND}${info.video_url}${info.video_url.includes('?') ? '&' : '?'}v=${info.render_id || info.generated_at || ''}`
    : null;
  const posterSrc = info.poster_url
    ? `${BACKEND}${info.poster_url}${info.poster_url.includes('?') ? '&' : '?'}v=${info.render_id || info.generated_at || ''}`
    : null;
  const isPublic = currentPublic === style;
  const history = (info.history || []).slice().reverse();

  return (
    <div className="ml-card" data-testid={`style-card-${style}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="ml-overline flex items-center gap-2">
            <span className={style === 'animated'
              ? 'inline-block w-2 h-2 rounded-full bg-purple-500'
              : 'inline-block w-2 h-2 rounded-full bg-amber-500'} />
            {style === 'animated' ? 'Style A · Animated' : 'Style B · Slideshow'}
            <span className="ml-1 text-[hsl(var(--muted-foreground))] font-normal">
              {style === 'animated' ? '· ~90–180s render' : '· ~25s render'}
            </span>
          </div>
          <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">{title}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{subtitle}</div>
        </div>
        {isPublic && (
          <span
            className="text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/50 rounded-full px-3 py-1 shrink-0"
            data-testid={`style-${style}-public-badge`}
          >
            Public
          </span>
        )}
      </div>

      <div className="mt-4">
        {hasVideo ? (
          <>
            {(() => {
              // Single staleness check shared by the overlay + banner.
              const last = info.music_track;
              const lastNorm = last && last.startsWith('custom:') ? '__custom__' : last;
              const isStale = !!(last && currentMusic && lastNorm !== currentMusic);
              return (
                <>
                  <div className="relative">
                    <video
                      src={videoSrc}
                      poster={posterSrc}
                      controls={!isStale}
                      playsInline
                      className={
                        'w-full aspect-square rounded-xl border border-[hsl(var(--accent))]/40 bg-black ' +
                        (isStale ? 'pointer-events-none opacity-70' : '')
                      }
                      data-testid={`video-${style}`}
                    />
                    {/* Hard block playback when the rendered audio is stale,
                        so families never watch a silent video by accident. */}
                    {isStale && (
                      <div
                        className="absolute inset-0 rounded-xl flex flex-col items-center justify-center text-center p-6 bg-[hsl(var(--primary)/0.78)] backdrop-blur-sm text-[#F7F3E8]"
                        data-testid={`video-${style}-stale-overlay`}
                      >
                        <Sparkles className="w-6 h-6 text-[hsl(var(--accent))]" />
                        <div className="font-serif text-lg mt-3 leading-tight">
                          Video needs regeneration
                          <span className="block text-[hsl(var(--accent))] italic">
                            to apply new music
                          </span>
                        </div>
                        <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#F4E5C2]/85 max-w-[220px]">
                          Soundtrack changed since this video was rendered.
                        </div>
                      </div>
                    )}
                  </div>
                  {isStale && (
                    <div
                      className="mt-3 rounded-md border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/8 px-4 py-3 text-xs text-[hsl(var(--primary)/0.8)] flex items-start gap-2"
                      data-testid={`video-${style}-music-stale`}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                      <span>
                        This video was rendered with a different soundtrack ({info.music_name || last}).
                        Click <strong>Regenerate</strong> to bake in your current selection.
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        ) : isBusy ? (
          <div className="aspect-square rounded-xl border border-dashed border-[hsl(var(--accent))]/60 bg-[hsl(var(--muted))] flex flex-col items-center justify-center text-center p-8" data-testid={`video-${style}-rendering`}>
            <div className="w-8 h-8 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" />
            <div className="font-serif text-lg mt-4 text-[hsl(var(--primary))]">
              {info.status === 'queued' ? 'Queued…' : 'Rendering…'}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2 max-w-[220px]">
              {elapsedSec >= 120
                ? 'Still rendering — you can leave this page and come back. We\'ll keep working in the background.'
                : 'Slideshow renders usually take 1–3 minutes.'}
            </div>
            {elapsedSec > 0 && (
              <div
                className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--accent))]"
                data-testid={`video-${style}-elapsed`}
              >
                {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')} elapsed
              </div>
            )}
            {/* Legacy "Cancel / Reset stuck job" underline removed —
                replaced by the prominent "Abandon render" pill in the
                action row below (iter95.9l). */}
          </div>
        ) : (
          <div className="aspect-square rounded-xl border border-dashed border-[hsl(var(--accent))]/60 bg-[hsl(var(--muted))] flex flex-col items-center justify-center text-center p-8" data-testid={`video-${style}-${isFailed ? 'failed' : 'empty'}`}>
            <Sparkles className={`w-6 h-6 ${isFailed ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--accent))]'}`} />
            <div className={`font-serif text-base mt-3 ${isFailed ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--primary))]'}`}>
              {isFailed ? 'Render failed' : 'Not generated yet'}
            </div>
            {isFailed && info.error && (
              <div className="text-xs text-[hsl(var(--destructive))] mt-2 max-w-xs leading-relaxed">{info.error}</div>
            )}
            {isFailed && (
              <button
                onClick={onGenerate}
                disabled={generating || anyBusy}
                className="mt-4 ml-btn-gold !py-2 !px-4 text-xs"
                data-testid={`retry-${style}-btn`}
              >
                Retry render
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-5">
        {style === 'slideshow' && memorialId && (
          <div className="mb-5">
            <SlideshowPhotoPicker memorialId={memorialId} />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={onGenerate}
          disabled={generating || anyBusy}
          className="ml-btn-gold !py-2 !px-4 text-xs"
          data-testid={`generate-${style}-btn`}
        >
          {isBusy ? 'Creating…' : hasVideo ? 'Regenerate' : 'Generate'}
        </button>
        {isBusy && onAbandon && (
          <button
            onClick={onAbandon}
            type="button"
            className="!py-2 !px-4 text-xs rounded-full border border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 transition-colors font-medium"
            data-testid={`abandon-${style}-btn`}
            title="Cancel this render — your previous video stays safe"
          >
            Abandon render
          </button>
        )}
        {hasVideo && !isPublic && (
          <button
            onClick={onSetPublic}
            className="ml-btn-outline !py-2 !px-4 text-xs"
            data-testid={`set-public-${style}-btn`}
          >
            Use this as public
          </button>
        )}
        {info.generated_at && (
          <div className="text-[10px] tracking-wide text-[hsl(var(--muted-foreground))]">
            {info.duration_sec?.toFixed(1)}s · {info.music_name ? `${info.music_name} · ` : ''}{new Date(info.generated_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[hsl(var(--primary)/0.08)]">
          <div className="ml-overline mb-3">Earlier versions</div>
          <ul className="space-y-2" data-testid={`video-${style}-history`}>
            {history.map((h) => (
              <li
                key={h.render_id}
                className="flex items-center gap-3 text-[12px]"
                data-testid={`history-item-${h.render_id}`}
              >
                <span className="font-serif text-[hsl(var(--primary))] flex-1 min-w-0 truncate">
                  {h.music_name || 'Silence'}
                </span>
                <span className="text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--primary)/0.45)]">
                  {h.generated_at ? new Date(h.generated_at).toLocaleDateString() : ''}
                </span>
                <button
                  type="button"
                  onClick={() => onRestore && onRestore(h.render_id)}
                  className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:underline"
                  data-testid={`restore-${h.render_id}`}
                >
                  Make current
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const LIFE_EVENT_CATEGORIES = [
  'school', 'work', 'marriage', 'child', 'grandchild',
  'hobby', 'achievement', 'place', 'favorite', 'event',
];

function LifeEventsEditor({ memorialId, onChange }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ category: 'school', title: '', description: '', start_date: '', end_date: '', location: '' });
  // Per-field validation errors — surfaces missing required fields in red
  // instead of letting "Add life event" silently do nothing.
  const [formErrors, setFormErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() =>
    api.get(`/memorials/${memorialId}/life-events`).then(({ data }) => {
      const sorted = [...(data.life_events || [])].sort(
        (a, b) => (a.start_date || '').localeCompare(b.start_date || '')
      );
      setEvents(sorted);
    }), [memorialId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    // Validate required fields and surface inline red errors before
    // calling the API. Silent no-ops are user-hostile.
    const errs = {};
    if (!form.title.trim()) {
      errs.title = 'A title is required — e.g. "Married Eleanor" or "Joined the Navy".';
    }
    if (!(form.start_date || '').trim()) {
      errs.start_date = 'A starting year is required so this chapter can sit on the timeline.';
    }
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      toast.error('Please fix the highlighted field(s) to add this chapter.');
      setTimeout(() => {
        const first = document.querySelector('[data-testid="life-event-add-title"], [data-testid^="life-event-add-start"]');
        if (first) { first.focus(); first.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      }, 0);
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      await api.post(`/memorials/${memorialId}/life-events`, form);
      setForm({ category: 'school', title: '', description: '', start_date: '', end_date: '', location: '' });
      toast.success('Event added · video marked for regeneration');
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const startEdit = (ev) => {
    setEditingId(ev.event_id);
    setEditForm({
      category: ev.category || 'event',
      title: ev.title || '',
      description: ev.description || '',
      start_date: ev.start_date || '',
      end_date: ev.end_date || '',
      location: ev.location || '',
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.patch(`/life-events/${editingId}`, editForm);
      setEditingId(null); setEditForm(null);
      toast.success('Event updated · video marked for regeneration');
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm('Are you sure you want to delete this life event?')) return;
    try {
      await api.delete(`/life-events/${ev.event_id}`);
      toast.success('Event deleted · video marked for regeneration');
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="ml-card" data-testid="life-events-editor">
      <div className="ml-overline">Life Journey · Events</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">Add, edit, or remove chapters</h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
        Changes here are saved right away. The Life Journey Video will be marked for regeneration; your current video keeps playing until you tap Regenerate.
      </p>

      {/* Add form */}
      <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="life-event-add-form">
        <select className="ml-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="life-event-add-category">
          {LIFE_EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <div>
          <input
            className={`ml-input ${formErrors.title ? 'ml-input--error' : ''}`}
            placeholder="Title (required)"
            value={form.title}
            onChange={(e) => {
              setForm({ ...form, title: e.target.value });
              if (formErrors.title) setFormErrors((p) => ({ ...p, title: undefined }));
            }}
            aria-invalid={!!formErrors.title}
            data-testid="life-event-add-title"
          />
          {formErrors.title && (
            <p role="alert" className="mt-1 text-xs text-red-600 font-medium" data-testid="life-event-add-title-error">
              {formErrors.title}
            </p>
          )}
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.6)] font-medium">
              From (year required) <span className="text-red-600" aria-hidden="true">*</span>
            </span>
            <PartialDateInput
              value={form.start_date}
              onChange={(v) => {
                setForm({ ...form, start_date: v });
                if (formErrors.start_date) setFormErrors((p) => ({ ...p, start_date: undefined }));
              }}
              testIdPrefix="life-event-add-start"
              className={`mt-1 ${formErrors.start_date ? 'ring-2 ring-red-600 ring-offset-2 ring-offset-[hsl(var(--background))] rounded-sm p-1' : ''}`}
            />
            {formErrors.start_date && (
              <p role="alert" className="mt-1 text-xs text-red-600 font-medium" data-testid="life-event-add-start-error">
                {formErrors.start_date}
              </p>
            )}
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.6)] font-medium">Until (optional)</span>
            <PartialDateInput
              value={form.end_date}
              onChange={(v) => setForm({ ...form, end_date: v })}
              testIdPrefix="life-event-add-end"
              className="mt-1"
            />
          </label>
        </div>
        <input className="ml-input sm:col-span-2" placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <textarea className="ml-input sm:col-span-2" rows={2} placeholder="Notes or description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <button onClick={add} disabled={saving} className="ml-btn-outline mt-3" data-testid="life-event-add-btn">
        <Plus className="w-4 h-4" /> Add life event
      </button>

      {/* List */}
      <div className="mt-6 divide-y divide-border/70">
        {events.length === 0 && (
          <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">No life events yet.</div>
        )}
        {events.map((ev) => {
          const isEditing = editingId === ev.event_id;
          return (
            <div key={ev.event_id} className="py-4" data-testid={`life-event-row-${ev.event_id}`}>
              {isEditing ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <select className="ml-input" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid={`life-event-edit-category-${ev.event_id}`}>
                    {LIFE_EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                  <input className="ml-input" placeholder="Title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid={`life-event-edit-title-${ev.event_id}`} />
                  <PartialDateInput
                    value={editForm.start_date}
                    onChange={(v) => setEditForm({ ...editForm, start_date: v })}
                    testIdPrefix={`life-event-edit-start-${ev.event_id}`}
                  />
                  <PartialDateInput
                    value={editForm.end_date}
                    onChange={(v) => setEditForm({ ...editForm, end_date: v })}
                    testIdPrefix={`life-event-edit-end-${ev.event_id}`}
                  />
                  <input className="ml-input sm:col-span-2" placeholder="Location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
                  <textarea className="ml-input sm:col-span-2" rows={2} placeholder="Notes" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                  <div className="sm:col-span-2 flex gap-3">
                    <button onClick={saveEdit} disabled={saving} className="ml-btn-primary" data-testid={`life-event-save-${ev.event_id}`}>
                      <Check className="w-4 h-4" /> Save changes
                    </button>
                    <button onClick={() => { setEditingId(null); setEditForm(null); }} className="ml-btn-outline" data-testid={`life-event-cancel-${ev.event_id}`}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="ml-overline !text-[hsl(var(--accent))]">{ev.category}</div>
                    <div className="font-serif text-lg text-[hsl(var(--primary))] truncate">{ev.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                      {formatPartialDate(ev.start_date)}
                      {ev.end_date ? ` — ${formatPartialDate(ev.end_date)}` : ''}
                      {ev.location ? ` · ${ev.location}` : ''}
                    </div>
                    {ev.description && (
                      <div className="text-sm text-[hsl(var(--primary))] mt-1 line-clamp-2">{ev.description}</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => startEdit(ev)}
                      className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--accent))] hover:underline"
                      data-testid={`life-event-edit-btn-${ev.event_id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(ev)}
                      className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--destructive))] hover:underline"
                      data-testid={`life-event-delete-btn-${ev.event_id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* LengthDriversPanel — small calm panel surfacing the four signals that
   actually drive video duration. Same numbers the renderer reads, so the
   admin can predict whether their tribute will land at 20s or 45s before
   they spend a render. */
function LengthDriversPanel({ drivers }) {
  const styleLabel = drivers.selected_style === 'slideshow' ? 'Photo Slideshow' : 'Portrait Tribute';
  const items = [
    {
      label: 'Photos selected',
      value: drivers.photos_selected,
      sub: drivers.selected_style === 'slideshow'
        ? 'Each photo adds ~3.6s to the slideshow.'
        : 'Used as the portrait — count drives the slideshow style.',
    },
    {
      label: 'Life events',
      value: drivers.events_count,
      sub: 'Used as chapter cues in the older Chapters style.',
    },
    {
      label: 'Story word count',
      value: drivers.story_word_count,
      sub: drivers.story_published
        ? 'Published Life Story — drives the Portrait Tribute length.'
        : 'No Life Story published yet — using the short summary.',
    },
    {
      label: 'Story chapters',
      value: drivers.story_chapters,
      sub: 'Each chapter is ~5–9 seconds. Up to 6 chapters supported.',
    },
  ];
  return (
    <div className="ml-card" data-testid="video-length-drivers">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="ml-overline">What affects length</div>
          <h3 className="font-serif text-lg mt-1 text-[hsl(var(--primary))]">
            Selected style: <span className="italic">{styleLabel}</span>
          </h3>
        </div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)]">
          Min {Math.round(drivers.min_duration_sec)}s
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div
            key={it.label}
            className="border border-[hsl(var(--accent))] rounded-sm p-4"
            style={{ backgroundColor: 'hsl(var(--accent))' }}
            data-testid={`length-driver-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
              {it.label}
            </div>
            <div className="font-serif text-3xl mt-1 text-[hsl(var(--primary))] font-bold">
              {it.value ?? 0}
            </div>
            <div className="text-[11px] mt-1.5 leading-relaxed text-[hsl(var(--primary))/0.85] font-medium">
              {it.sub}
            </div>
          </div>
        ))}
      </div>

      <p
        className="mt-5 text-[12px] italic font-light text-[hsl(var(--primary)/0.7)] leading-relaxed"
        data-testid="length-drivers-hint"
      >
        {drivers.hint}
      </p>
    </div>
  );
}


/* CustomVideoMusicCard — surfaces the per-memorial custom audio upload
   directly inside the Video tab so admins can override the default video
   music without leaving the workflow. The same upload also drives ambient
   playback on the public memorial. */
function CustomVideoMusicCard({ memorialId, customMusic, defaultMusicTrack, onChange, onSetDefault }) {
  const [uploading, setUploading] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const fileRef = React.useRef(null);

  const isDefault = (defaultMusicTrack || '').startsWith('custom:');

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 75 * 1024 * 1024) {
      toast.error('File is over 75 MB. Please pick a smaller audio file.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(
        `/memorials/${memorialId}/music/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Custom music uploaded — used in your next render.');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the custom music? The next video render will use the default track again.')) return;
    try {
      await api.delete(`/memorials/${memorialId}/music/custom`);
      toast.success('Custom music removed');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="ml-card" data-testid="video-custom-music">
      <div className="ml-overline">Music</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
        Upload your own music
      </h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-xl">
        Upload a song the family loved. It will play under the Life Journey video
        <span className="text-[hsl(var(--primary))] font-medium"> and </span>
        on the public memorial's ambient player.
      </p>
      <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)] font-semibold">
        mp3 · m4a · wav · max 75 MB
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => !uploading && fileRef.current?.click()}
          disabled={uploading}
          className="ml-btn-gold !py-2.5 !px-5 text-xs"
          data-testid="video-custom-music-upload-btn"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading…' : (customMusic ? 'Replace track' : 'Upload your own music')}
        </button>
        <input
          ref={fileRef}
          type="file"
          // Mobile-friendly: any audio MIME + extension hints so iOS / Android
          // open the Files picker (not the camera/gallery).
          accept="audio/*,.mp3,.m4a,.wav,.aac"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="video-custom-music-file"
        />
      </div>

      {customMusic && (
        <div
          className="mt-5 p-4 rounded-sm border border-[hsl(var(--accent)/0.4)] bg-[hsl(var(--accent)/0.08)]"
          data-testid="video-custom-music-status"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[hsl(var(--primary))] truncate">
                  {customMusic.filename}
                </div>
                {isDefault && (
                  <span
                    className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-white"
                    data-testid="video-custom-music-default-badge"
                  >
                    Saved as default
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] text-[hsl(var(--primary)/0.7)] font-medium">
                {Math.round((customMusic.size || 0) / 1024)} KB · uploaded{' '}
                {new Date(customMusic.uploaded_at).toLocaleDateString()}
              </div>
              <div className="mt-3">
                <audio
                  controls
                  src={`${BACKEND_URL}/api/memorials/${memorialId}/music/custom.mp3?v=${encodeURIComponent(customMusic.uploaded_at || '')}`}
                  className="w-full max-w-[420px]"
                  data-testid="video-custom-music-preview"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {!isDefault && (
                <button
                  type="button"
                  disabled={savingDefault}
                  onClick={async () => {
                    if (!onSetDefault) return;
                    setSavingDefault(true);
                    try { await onSetDefault(); } finally { setSavingDefault(false); }
                  }}
                  className="ml-btn-gold !py-2 text-xs"
                  data-testid="video-custom-music-set-default"
                >
                  <Check className="w-4 h-4" />
                  {savingDefault ? 'Saving…' : 'Use this music'}
                </button>
              )}
              <button
                type="button"
                onClick={remove}
                className="ml-btn-outline !py-2 text-xs"
                data-testid="video-custom-music-remove"
              >
                Remove
              </button>
            </div>
          </div>
          <p className="mt-3 text-[12px] text-[hsl(var(--primary)/0.78)] font-medium">
            {isDefault
              ? 'This song is now the default soundtrack — it will play on the public memorial and the next Life Journey video render. Persists across devices and admin sessions.'
              : 'Click "Use this music" to make this the default soundtrack across this memorial — public player and Life Journey video.'}
          </p>
        </div>
      )}
    </div>
  );
}
